import { escapeHtml } from "../game/util/escape.js";
const FLASH_MS = 260;
const WINDOW_HALF = 0.14;
const CROSS_SECONDS = 2.4;
const EDGE_DEBOUNCE_MS = 180;
const EVENT_FRAME_WINDOW = 10;
const ONSET_TRANSIENT = 0.12;
const ONSET_ENERGY_RISE = 0.1;
const ONSET_QUIET_PREV = 0.2;
const REQUIRED_SAMPLES = 6;
export class AcPopPanel extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "clusterId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "pop"
        });
        Object.defineProperty(this, "description", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        Object.defineProperty(this, "pronunciation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ""
        });
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "startTs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "ballX", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0.5
        });
        Object.defineProperty(this, "inWindow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "consumedThisPass", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "lastEdgeAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: -Infinity
        });
        Object.defineProperty(this, "prevPop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "recent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "flash", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "counts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { sample: 0, control: 0, duplicate: 0 }
        });
        Object.defineProperty(this, "prevEnergy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    connectedCallback() {
        this.clusterId = this.getAttribute("cluster") ?? "pop";
        this.description = this.getAttribute("description") ?? "";
        this.pronunciation = this.getAttribute("pronunciation") ?? "";
        this.style.position = "fixed";
        this.style.inset = "0";
        this.style.zIndex = "1100";
        this.style.pointerEvents = "auto";
        this.render();
        this.start();
    }
    disconnectedCallback() {
        this.stop();
    }
    update(snap) {
        this.recent.push(snap.features);
        if (this.recent.length > EVENT_FRAME_WINDOW) {
            this.recent.splice(0, this.recent.length - EVENT_FRAME_WINDOW);
        }
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const f = snap.features;
        const energy = f.energy ?? 0;
        const transient = f.transient ?? 0;
        const rise = energy - this.prevEnergy;
        const isPop = transient >= ONSET_TRANSIENT ||
            (this.prevEnergy < ONSET_QUIET_PREV && rise >= ONSET_ENERGY_RISE);
        if (isPop && !this.prevPop && now - this.lastEdgeAt > EDGE_DEBOUNCE_MS) {
            this.lastEdgeAt = now;
            this.onPlosive(now);
        }
        this.prevPop = isPop;
        this.prevEnergy = energy;
    }
    onPlosive(now) {
        let outcome;
        if (this.inWindow) {
            if (this.consumedThisPass) {
                outcome = "duplicate";
            }
            else {
                outcome = "sample";
                this.consumedThisPass = true;
            }
        }
        else {
            outcome = "control";
        }
        this.counts[outcome] += 1;
        const color = outcome === "sample"
            ? "#4ade80"
            : outcome === "control"
                ? "#f87171"
                : "#fbbf24";
        this.flash = { color, until: now + FLASH_MS };
        this.dispatchEvent(new CustomEvent("poppanel-event", {
            detail: {
                clusterId: this.clusterId,
                outcome,
                frames: [...this.recent],
            },
            bubbles: true,
        }));
        this.updateReadout();
        if (this.counts.sample >= REQUIRED_SAMPLES) {
            this.autoAdvance();
        }
    }
    autoAdvance() {
        console.info("[ac-pop-panel] enough valid samples collected — auto-advancing");
        this.dispatchEvent(new CustomEvent("poppanel-done", {
            detail: { clusterId: this.clusterId },
            bubbles: true,
        }));
    }
    start() {
        this.running = true;
        this.startTs =
            typeof performance !== "undefined" ? performance.now() : Date.now();
        const loop = () => {
            if (!this.running)
                return;
            this.tick();
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
    }
    stop() {
        this.running = false;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
    }
    tick() {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const t = (now - this.startTs) / 1000;
        const period = CROSS_SECONDS * 2;
        const phase = (t % period) / period;
        this.ballX = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        const nowInWindow = Math.abs(this.ballX - 0.5) <= WINDOW_HALF;
        if (nowInWindow && !this.inWindow) {
            this.consumedThisPass = false;
        }
        this.inWindow = nowInWindow;
        this.draw(now);
    }
    draw(now) {
        const canvas = this.querySelector("[data-pop-canvas]");
        if (!canvas)
            return;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const cw = canvas.clientWidth || 520;
        const ch = canvas.clientHeight || 120;
        if (canvas.width !== Math.floor(cw * dpr) ||
            canvas.height !== Math.floor(ch * dpr)) {
            canvas.width = Math.floor(cw * dpr);
            canvas.height = Math.floor(ch * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        const pad = 24;
        const trackY = ch / 2;
        const trackW = cw - pad * 2;
        const x0 = pad;
        ctx.strokeStyle = "rgba(255,255,255,0.15)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x0, trackY);
        ctx.lineTo(x0 + trackW, trackY);
        ctx.stroke();
        const winCx = x0 + trackW * 0.5;
        const winW = trackW * (WINDOW_HALF * 2);
        ctx.fillStyle = this.inWindow
            ? "rgba(74,222,128,0.18)"
            : "rgba(34,211,238,0.14)";
        ctx.fillRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.strokeStyle = this.inWindow
            ? "rgba(74,222,128,0.7)"
            : "rgba(34,211,238,0.5)";
        ctx.lineWidth = 2;
        ctx.strokeRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = "600 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("POP HERE", winCx, pad - 8);
        const bx = x0 + trackW * this.ballX;
        const flashing = this.flash && now < this.flash.until;
        const glow = flashing ? this.flash.color : "#22d3ee";
        const flashT = flashing ? 1 - (this.flash.until - now) / FLASH_MS : 0;
        const r = 14 + (flashing ? (1 - flashT) * 10 : 0);
        ctx.shadowColor = glow;
        ctx.shadowBlur = flashing ? 24 : 10;
        ctx.fillStyle = flashing ? glow : "#22d3ee";
        ctx.beginPath();
        ctx.arc(bx, trackY, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        if (flashing) {
            ctx.strokeStyle = glow;
            ctx.lineWidth = 3;
            ctx.globalAlpha = 1 - flashT;
            ctx.beginPath();
            ctx.arc(bx, trackY, r + flashT * 26, 0, Math.PI * 2);
            ctx.stroke();
            ctx.globalAlpha = 1;
        }
    }
    updateReadout() {
        const el = this.querySelector("[data-pop-readout]");
        if (el) {
            el.innerHTML =
                `<span style="color:#4ade80">✓ ${this.counts.sample} samples</span>` +
                    ` &nbsp; <span style="color:#f87171">✗ ${this.counts.control} out-of-window</span>` +
                    ` &nbsp; <span style="color:#fbbf24">⟳ ${this.counts.duplicate} duplicates</span>`;
        }
        const done = this.querySelector("[data-pop-done]");
        if (done) {
            done.disabled = this.counts.sample === 0;
            done.style.opacity = this.counts.sample === 0 ? ".5" : "1";
        }
    }
    render() {
        const safeDesc = escapeHtml(this.description);
        const safePron = escapeHtml(this.pronunciation);
        const descBlock = this.description || this.pronunciation
            ? `<div style="margin:0 0 14px;padding:10px 12px;border-radius:10px;
                      background:rgba(34,211,238,.08);border:1px solid #22d3ee">
                     ${this.description
                ? `<div style="font-size:13px;line-height:1.5;margin-bottom:${this.pronunciation ? "6px" : "0"}">
                          ${safeDesc}</div>`
                : ""}
                     ${this.pronunciation
                ? `<div style="font-size:12px;line-height:1.5;opacity:.9">
                          <b style="color:#22d3ee">How:</b> ${safePron}</div>`
                : ""}
                   </div>`
            : "";
        this.innerHTML = `
              <div style="position:absolute;inset:0;background:rgba(0,0,0,.6);
                  display:flex;align-items:center;justify-content:center">
                <div style="background:#0d1024;border:1px solid #2a2f4a;border-radius:16px;
                    width:min(92vw,560px);color:#fff;padding:20px 22px">
                  <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
                    <span style="font-size:20px">💥</span>
                    <span style="flex:1;font-size:18px;font-weight:800">Pop Panel</span>
                    <button data-pop-cancel
                        style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                        border-radius:8px;border:1px solid #f87171;background:#1a1f3a;
                        color:#fff;font-size:13px;font-weight:600">Cancel</button>
                  </div>
                  ${descBlock}
                  <p style="opacity:.8;font-size:13px;margin:0 0 14px;line-height:1.5">
                    Pop your lips (<b>p! p!</b>) each time the ball is inside the
                    highlighted window — and stay quiet when it's outside. Green
                    flashes are captured samples; red means you popped too early
                    or late; amber means a duplicate/false pop.
                  </p>
                  <canvas data-pop-canvas
                      style="display:block;width:100%;height:120px;border-radius:12px;
                      background:radial-gradient(circle at 50% 50%,rgba(34,211,238,.08),rgba(10,12,24,.9))"></canvas>
                  <div data-pop-readout style="text-align:center;margin-top:12px;
                      font-size:13px;font-weight:600">
                    <span style="color:#4ade80">✓ 0 samples</span>
                    &nbsp; <span style="color:#f87171">✗ 0 out-of-window</span>
                    &nbsp; <span style="color:#fbbf24">⟳ 0 duplicates</span>
                  </div>
                  <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:16px">
                   <button data-pop-skip
                       style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                       border-radius:10px;border:1px solid #fbbf24;background:#1a1f3a;
                       color:#fff;font-size:14px;font-weight:700;margin-right:auto">
                       Skip this sound</button>
                    <button data-pop-done disabled
                        style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                        border-radius:10px;border:1px solid #4ade80;background:#1a1f3a;
                        color:#fff;font-size:14px;font-weight:700;opacity:.5">
                        Save samples</button>
                  </div>
                </div>
              </div>`;
        this.querySelector("[data-pop-cancel]")?.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("poppanel-cancel", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
        this.querySelector("[data-pop-done]")?.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("poppanel-done", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
        this.querySelector("[data-pop-skip]")?.addEventListener("click", () => {
            console.info(`[ac-pop-panel] user skipped calibrating '${this.clusterId}'`);
            this.dispatchEvent(new CustomEvent("poppanel-skip", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
    }
}
customElements.define("ac-pop-panel", AcPopPanel);
